import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareCustomHeaders } from "../utils/baseQueryHeaders";

// Types
export type CustomerFieldId = number;
export type ProcessorSpecId = number;

export interface FieldTypeChoice {
  value: string;
  label: string;
}

export interface ListValue {
  value: string;
  label: string;
}

export interface CustomerField {
  id: CustomerFieldId;
  field_id: string;
  name: string | null;
  type: string | null;
  entity_id: string | null;
  description: string | null;
  list_values: ListValue[] | null;
  processor_specs_count: number;
}

export interface CustomerFieldsFilters {
  search?: string;
  entity_ids?: string[];
  types?: string[];
}

export interface CustomerFieldsResponse {
  fields: CustomerField[];
  field_type_choices: FieldTypeChoice[];
  can_sync_from_inriver: boolean;
}

export interface CustomerFieldFilterOptionsResponse {
  entity_ids: string[];
  has_empty_entity: boolean;
  types: string[];
  has_empty_type: boolean;
}

export interface SyncFromInriverRequest {
  sync_type: "fields" | "fieldsets";
}

export interface SyncFromInriverResponse {
  success: boolean;
  object_name?: string;
  created_count?: number;
  updated_count?: number;
  message?: string;
}

export interface ProcessorChoice {
  value: string;
  label: string;
  group: string | null;
}

export interface ProcessorSpec {
  id: ProcessorSpecId;
  customer_field_id: CustomerFieldId;
  name: string | null;
  options: Record<string, unknown> | null;
  namespace_name: string;
}

export interface ProcessorSpecsResponse {
  processor_specs: ProcessorSpec[];
  processor_choices: ProcessorChoice[];
}

export interface OptionFieldSchema {
  name: string;
  label: string;
  type: "text" | "choice" | "boolean" | "integer";
  required: boolean;
  help_text: string | null;
  choices?: { value: string; label: string }[];
}

export interface OptionsSchemaResponse {
  schema: OptionFieldSchema[];
}

export interface NamespacesResponse {
  namespaces: string[];
}

// Request types
export interface CreateCustomerFieldRequest {
  field_id: string;
  name?: string;
  type?: string | null;
  entity_id?: string | null;
  description?: string;
  list_values?: ListValue[] | null;
}

export interface UpdateCustomerFieldRequest {
  id: CustomerFieldId;
  name?: string;
  type?: string | null;
  entity_id?: string | null;
  description?: string;
  list_values?: ListValue[] | null;
}

export interface CreateProcessorSpecRequest {
  customer_field_id: CustomerFieldId;
  name?: string | null;
  options?: Record<string, unknown> | null;
  namespace_name?: string;
}

export interface UpdateProcessorSpecRequest {
  id: ProcessorSpecId;
  name?: string | null;
  options?: Record<string, unknown> | null;
}

// API definition
export const customerFieldApi = createApi({
  reducerPath: "customerFieldApi",
  tagTypes: ["CustomerField", "ProcessorSpec", "Namespaces"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/_internal",
    prepareHeaders: prepareCustomHeaders,
  }),
  endpoints: (build) => ({
    // CustomerField endpoints
    getCustomerFields: build.query<
      CustomerFieldsResponse,
      CustomerFieldsFilters
    >({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters?.search) {
          params.set("search", filters.search);
        }
        if (filters?.entity_ids?.length) {
          params.set("entity_ids", filters.entity_ids.join(","));
        }
        if (filters?.types?.length) {
          params.set("types", filters.types.join(","));
        }
        const queryString = params.toString();
        return `/customer/fields/${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: (result) => {
        if (result) {
          return [
            ...result.fields.map(({ id }) => ({
              type: "CustomerField" as const,
              id,
            })),
            { type: "CustomerField", id: "LIST" },
          ];
        }
        return [{ type: "CustomerField", id: "LIST" }];
      },
    }),

    getCustomerFieldFilterOptions: build.query<
      CustomerFieldFilterOptionsResponse,
      void
    >({
      query: () => "/customer/fields/filter-options",
      providesTags: [{ type: "CustomerField", id: "FILTER_OPTIONS" }],
    }),

    createCustomerField: build.mutation<
      CustomerField,
      CreateCustomerFieldRequest
    >({
      query: (body) => ({
        url: "/customer/fields/create",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "CustomerField", id: "LIST" },
        { type: "CustomerField", id: "FILTER_OPTIONS" },
      ],
    }),

    updateCustomerField: build.mutation<
      CustomerField,
      UpdateCustomerFieldRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/customer/fields/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "CustomerField", id },
        { type: "CustomerField", id: "LIST" },
        { type: "CustomerField", id: "FILTER_OPTIONS" },
      ],
    }),

    deleteCustomerField: build.mutation<void, CustomerFieldId>({
      query: (id) => ({
        url: `/customer/fields/${id}/delete`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "CustomerField", id: "LIST" },
        { type: "CustomerField", id: "FILTER_OPTIONS" },
      ],
    }),

    // ProcessorSpec endpoints
    getProcessorSpecs: build.query<ProcessorSpecsResponse, CustomerFieldId>({
      query: (customerFieldId) =>
        `/extract/processor-specs/?customer_field_id=${customerFieldId}`,
      providesTags: (result, error, customerFieldId) => {
        if (result) {
          return [
            ...result.processor_specs.map(({ id }) => ({
              type: "ProcessorSpec" as const,
              id,
            })),
            { type: "ProcessorSpec", id: `FIELD-${customerFieldId}` },
          ];
        }
        return [{ type: "ProcessorSpec", id: `FIELD-${customerFieldId}` }];
      },
    }),

    createProcessorSpec: build.mutation<
      ProcessorSpec,
      CreateProcessorSpecRequest
    >({
      query: (body) => ({
        url: "/extract/processor-specs/create",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { customer_field_id }) => [
        { type: "ProcessorSpec", id: `FIELD-${customer_field_id}` },
        { type: "CustomerField", id: customer_field_id },
      ],
    }),

    updateProcessorSpec: build.mutation<
      ProcessorSpec,
      UpdateProcessorSpecRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/extract/processor-specs/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ProcessorSpec", id },
      ],
    }),

    deleteProcessorSpec: build.mutation<
      void,
      { id: ProcessorSpecId; customer_field_id: CustomerFieldId }
    >({
      query: ({ id }) => ({
        url: `/extract/processor-specs/${id}/delete`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id, customer_field_id }) => [
        { type: "ProcessorSpec", id },
        { type: "ProcessorSpec", id: `FIELD-${customer_field_id}` },
        { type: "CustomerField", id: customer_field_id },
      ],
    }),

    // Options schema endpoint
    getOptionsSchema: build.query<OptionsSchemaResponse, string>({
      query: (processorName) =>
        `/extract/processor-specs/options-schema?name=${encodeURIComponent(
          processorName
        )}`,
    }),

    // Namespaces endpoint
    getNamespaces: build.query<NamespacesResponse, void>({
      query: () => "/extract/processor-specs/namespaces",
      providesTags: [{ type: "Namespaces", id: "LIST" }],
    }),

    // InRiver sync endpoint
    syncFromInriver: build.mutation<
      SyncFromInriverResponse,
      SyncFromInriverRequest
    >({
      query: (body) => ({
        url: "/customer/fields/sync-from-inriver",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "CustomerField", id: "LIST" },
        { type: "CustomerField", id: "FILTER_OPTIONS" },
      ],
    }),
  }),
});

export const {
  useGetCustomerFieldsQuery,
  useGetCustomerFieldFilterOptionsQuery,
  useCreateCustomerFieldMutation,
  useUpdateCustomerFieldMutation,
  useDeleteCustomerFieldMutation,
  useGetProcessorSpecsQuery,
  useCreateProcessorSpecMutation,
  useUpdateProcessorSpecMutation,
  useDeleteProcessorSpecMutation,
  useGetOptionsSchemaQuery,
  useGetNamespacesQuery,
  useSyncFromInriverMutation,
} = customerFieldApi;
